import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ClientAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ClientAuthProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('client_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(
        `${API}/client-auth/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setClient(response.data);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, phone, password) => {
    try {
      await axios.post(`${API}/client-auth/signup`, {
        name,
        email,
        phone,
        password
      });
      
      // Auto login after signup
      return await login(email, password);
    } catch (error) {
      console.error('Signup failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/client-auth/login`, {
        email,
        password
      });
      
      const { access_token, client: clientData } = response.data;
      localStorage.setItem('client_token', access_token);
      setToken(access_token);
      setClient(clientData);
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('client_token');
    setToken(null);
    setClient(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <ClientAuthContext.Provider value={{ client, token, loading, signup, login, logout, getAuthHeader }}>
      {children}
    </ClientAuthContext.Provider>
  );
};

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within ClientAuthProvider');
  }
  return context;
};